<template>
  <nav :class="['nav-bar-mobile', { open: menuOpen }, { scrollUp }]">
    <div class="nav-bar-mobile-header">
      <div class="nav-bar-mobile__left">
        <button
          class="nav-bar-mobile__left__button"
          @click="() => (menuOpen = !menuOpen)"
        >
          <!-- <Transition name="fade"> -->
          <icon
            data="@icon/hamburger.svg"
            v-if="!menuOpen"
            color="var(--color-white)"
          />
          <icon
            class="nav-bar-mobile__left__button__icon"
            data="@icon/close.svg"
            v-else
            color="var(--color-white)"
          />
          <!-- </Transition> -->
        </button>
        <button
          v-if="caseStudiesOpen && tvaMq == 'mobile'"
          @click.native="caseStudiesOpen = false"
        >
          <icon
            class="nav-bar-mobile__left__back-icon"
            data="@icon/arrow-down-small.svg"
          />
        </button>
        <a href="https://eni.com" v-else>
          <icon
            data="@icon/logo.svg"
            color="var(--color-white)"
            width="4.1rem"
            height="1.5rem"
          />
        </a>
      </div>
    </div>

    <div v-if="!mobile" class="nav-bar-mobile__right">
      <LanguageSwitcher
        class="nav-bar-mobile__menu__language"
        :languages="$tm('languages')"
      />

      <SocialSharing />
    </div>

    <Transition name="fade">
      <div class="nav-bar-mobile__menu" v-if="menuOpen">
        <div class="nav-bar-mobile__menu__left">
          <LanguageSwitcher
            v-if="mobile"
            class="nav-bar-mobile__menu__language"
            :languages="$tm('languages')"
          />

          <SocialSharing
            class="nav-bar-mobile__menu__social"
            v-if="mobile"
            :smallscreen="mobile"
          />

          <router-link
            v-for="(page, index) in pageLinks"
            class="nav-bar-mobile__menu__link"
            :key="page + '-' + index"
            :to="{ params: { page } }"
            @click.native="menuOpen = false"
          >
            <div v-if="index" class="nav-bar-mobile__menu__link__pillar">
              {{ $t("navBar.pillar") }} {{ $tm("navBar.count")[index - 1] }}
            </div>
            {{ $t(`navigation.${page}`) }}</router-link
          >
          <button
            class="nav-bar-mobile__menu__link nav-bar-mobile__menu__link-case-studies"
            key="case-studies"
            @click.native="caseStudiesOpen = !caseStudiesOpen"
          >
            {{ $t(`navBar.caseStudies`) }}

            <icon
              class="case-studies__icon"
              data="@icon/arrow-down-small.svg"
            />
          </button>
        </div>
        <div class="case-studies" v-if="caseStudiesOpen">
          <router-link
            v-for="(caseStudy, index) in caseStudyLinks"
            class="case-studies__link"
            :key="caseStudy + '-' + index"
            :to="{ query: { caseStudy: caseStudy } }"
            @click.native="
              () => {
                caseStudiesOpen = false;
                menuOpen = false;
              }
            "
          >
            <div class="case-studies__link__pillar">
              {{ $t("navBar.caseStudies") }}
            </div>
            {{ $t(`navigationCaseStudies.${caseStudy}`) }}</router-link
          >
        </div>
      </div>
    </Transition>
  </nav>
</template>

<script setup>
import { computed, inject, ref, watch } from "vue";

import LanguageSwitcher from "./LanguageSwitcher.vue";
import SocialSharing from "./SocialSharing.vue";

defineProps({
  pageLinks: {
    type: Array,
    default: () => [],
  },
  caseStudyLinks: {
    type: Array,
    default: () => [],
  },
  languages: {
    type: Array,
    default: () => [],
  },
  scrollUp: {
    type: Boolean,
    default: false,
  },
});

const tvaMq = inject("$tvaMq");
const mobile = computed(() => tvaMq.value === "mobile");

const menuOpen = ref(false);
const caseStudiesOpen = ref(false);

watch(
  () => menuOpen.value,
  () => {
    document.body.style.overflow = menuOpen.value ? "hidden" : "initial";
    caseStudiesOpen.value = false;
  }
);
</script>

<style lang="scss" scoped>
.nav-bar-mobile {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 5;
  height: var(--navbar-height);
  width: 100%;
  padding: toRem(16) toRem(16) toRem(16) toRem(32);
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--color-white);
  transition: background-color 0.5s;

  &.scrollUp {
    position: fixed;
    background: var(--background-base);
    animation: scroll 0.5s ease;
    @keyframes scroll {
      from {
        transform: translateY(-100%);
      }
      to {
        transform: translateY(0);
      }
    }
  }

  .mobile & {
    padding: 1rem;
  }

  &-header {
    width: 100%;
  }
  &.open {
    background-color: var(--background-base);
    border-bottom: 1px solid var(--color-light-grey);
  }

  &__left,
  &__right {
    display: flex;
    align-items: center;
  }

  &__left {
    gap: 1rem;

    &__button {
      width: toRem(20);
      height: toRem(20);
      // transform: translateX(toRem(-10));
      position: relative;

      & > svg {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        min-width: 100%;
        min-height: 100%;
        opacity: 0.8;
      }
      &:hover {
        & > svg {
          opacity: 1;
        }
      }
    }

    .mobile & {
      width: 100%;
      justify-content: space-between;
      flex-direction: row-reverse;
      &__back-icon {
        transform: rotate(90deg);
        &:hover {
          fill: var(--brand-stronger);
        }
      }
    }
  }

  &__menu {
    position: fixed;
    top: var(--navbar-height);
    left: 0;
    display: flex;
    background-color: var(--background-base);

    width: 100%;
    height: calc(100% - var(--navbar-height));

    &__social {
      position: absolute;
      bottom: 1rem;
      right: 1rem;
    }

    &__left {
      display: flex;
      flex-direction: column;
      gap: 4vh;
      padding: 4vh toRem(70);
      .mobile & {
        padding: 1.5rem;
        width: 100%;
      }
    }
    &__language {
      width: fit-content;
      align-self: center;
      display: flex;

      .mobile & {
        position: absolute;
        bottom: 1rem;
        left: 1rem;
        height: 1.35rem;
        :deep(button) {
          padding-top: 0;
          padding-bottom: 0;
          &:first-of-type {
            padding-left: 0;
          }
        }
      }
    }

    &__link {
      font-family: EniTab;
      font-size: 1.5rem;
      font-weight: 400;
      line-height: 1.5rem;
      white-space: nowrap;
      text-align: left;

      &:hover {
        .nav-bar-mobile__menu__link__pillar {
          color: #fff;
        }
        color: var(--brand-stronger);
      }
      .tablet & {
        font-size: 1.25rem;
      }
      .mobile & {
        width: 100% !important;
        font-size: 1rem;
      }

      &-case-studies {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        :deep(.svg-icon) {
          transform: rotate(-90deg);
        }
      }

      .desktop &,
      .large &,
      .mobile & {
        width: toRem(205);
      }

      &__pillar {
        @include overlay-s;

        color: var(--color-light-grey);
      }
    }
  }
  .case-studies {
    padding: calc(4vh + 3.8rem) 2.5rem;
    border-left: 1px solid var(--data-viz-axis-light, rgba(254, 255, 255, 0.4));
    &__link {
      font-family: EniTab;
      font-size: 1.25rem;
      font-style: normal;
      font-weight: 400;
      line-height: 1.25rem;
      margin-bottom: 2.5rem;
      display: block;
      &:hover {
        .case-studies__link__pillar {
          color: #fff;
        }
        color: var(--brand-stronger);
      }
      &__pillar {
        @include overlay-s;
        margin-bottom: 0.5rem;
      }
    }
    .mobile & {
      position: fixed;
      top: 56px;
      left: 0;
      width: 100vw;
      height: calc(100vh - 56px);
      background: var(--background-base);
      pointer-events: none;
      padding: 1.5rem;
      border: none;
      &__link {
        font-size: 1rem;
        line-height: 1.25rem;
        pointer-events: all;
        &__pillar {
          font-size: 0.75rem;
          font-style: normal;
          font-weight: 600;
          line-height: 1.125rem;
        }
      }
    }
  }
}
</style>
